/* cSpell:ignore viewability */

import React, {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { type ScrollViewProps, StyleSheet } from 'react-native';
import { View } from 'react-native';
import { theme } from '@garnish/constants';
import { FlashList } from '@shopify/flash-list';

import {
  useRailNavigation,
  useRailOffsetStyles,
  useRailViewableState,
} from './hooks';
import {
  HorizontalScrollRailFooterText,
  HorizontalScrollRailHeader,
  useRailItemSeparator,
} from './subcomponents';

// ─────────────────────────────────────────────────────────────────────────────

export const HorizontalScrollRail = (props: HorizontalScrollRailProps) => {
  const {
    heading = '',
    subtitle = '',
    footerText = '',
    headerVariation,
    gap = RAIL_DEFAULT_GAP,
    outerOffset = RAIL_DEFAULT_OUTER_OFFSET,
    initialScrollIndex,
    showNavControls = true,
    children: railItems,
    withoutHeaderBorder,
    itemVisiblePercentThreshold = 90,
    trackViewableState: trackViewableStateFromProps,
    count = 0,
    countPosition,
    ...scrollViewProps
  } = props;

  // ─────────────────────────────────────────────────────────────────────

  const railOffsetStyles = useRailOffsetStyles(outerOffset);
  const ItemSeparatorComponent = useRailItemSeparator(gap);
  const railViewableState = useRailViewableState(railItems);
  const { viewableState, trackViewableState } = railViewableState;
  const { railRef, scrollToPrevItem, scrollToNextItem, scrollToInitialIndex } =
    useRailNavigation({
      viewableState,
      outerOffset,
      initialScrollIndex,
    });

  const viewabilityConfig = useMemo(
    () => ({ itemVisiblePercentThreshold }),
    [itemVisiblePercentThreshold],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOnViewableItemsChanged = useCallback<OnViewableItemsChanged>(
    (event) => {
      trackViewableState(event);
      trackViewableStateFromProps?.(event);
    },
    [trackViewableStateFromProps, trackViewableState],
  );

  // ─────────────────────────────────────────────────────────────────────

  const shouldRenderHeader = heading || showNavControls;
  const shouldShowNavControls =
    showNavControls && !viewableState.allItemsAreVisible;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View>
      {shouldRenderHeader ? (
        <HorizontalScrollRailHeader
          heading={heading}
          subtitle={subtitle}
          headerVariation={headerVariation}
          showNavControls={shouldShowNavControls}
          disablePrevBtn={viewableState.isAtStart}
          disableNextBtn={viewableState.isAtEnd}
          onPrevBtnPress={scrollToPrevItem}
          onNextBtnPress={scrollToNextItem}
          withoutHeaderBorder={withoutHeaderBorder}
          countPosition={countPosition}
          count={count}
        />
      ) : null}

      <View style={railOffsetStyles.railStyle}>
        <FlashList<ReactNode>
          ref={railRef}
          data={railItems}
          ItemSeparatorComponent={ItemSeparatorComponent}
          viewabilityConfig={viewabilityConfig}
          renderItem={renderItem}
          scrollEventThrottle={16}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={railOffsetStyles.containerStyle}
          onContentSizeChange={scrollToInitialIndex}
          onViewableItemsChanged={handleOnViewableItemsChanged}
          {...scrollViewProps}
        >
          {railItems}
        </FlashList>
      </View>

      {footerText ? (
        <HorizontalScrollRailFooterText>
          {footerText}
        </HorizontalScrollRailFooterText>
      ) : null}
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const RAIL_DEFAULT_GAP = theme.spacing['4'];
const RAIL_DEFAULT_OUTER_OFFSET = 0;

// ─── Utils ───────────────────────────────────────────────────────────────────

const renderItem: FlashListProps['renderItem'] = (props) => (
  <View style={styles.itemContainer}>{props.item}</View>
);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  itemContainer: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HorizontalScrollRailProps = Readonly<{
  children: readonly ReactNode[];
  gap?: number;
  outerOffset?: number;
  footerText?: string;
  count?: number;
  countPosition?: ComponentProps<
    typeof HorizontalScrollRailHeader
  >['countPosition'];
  initialScrollIndex?: number;
  showNavControls?: boolean;
  itemVisiblePercentThreshold?: number;
  trackViewableState?: FlashListProps['onViewableItemsChanged'];
}> &
  Omit<
    ComponentProps<typeof HorizontalScrollRailHeader>,
    'disablePrevBtn' | 'disableNextBtn' | 'count' | 'countPosition'
  > &
  Omit<ScrollViewProps, 'horizontal' | 'contentContainerStyle'>;

type OnViewableItemsChanged = NonNullable<
  ComponentProps<typeof FlashList>['onViewableItemsChanged']
>;

type FlashListProps = ComponentProps<typeof FlashList<ReactNode>>;
